import { useMemo } from 'react';
import { DefaultLayout } from 'core/layouts';
import { MetaApprovalStatusDefinition, WhatsAppContentDefinition } from 'modules/graphql';
import styled from 'styled-components';

import {
  DetailsEditSaveButton,
  EditableHeader,
  EditorContentContainer,
  ErrorWarningBanner,
  LayoutContentContainer,
  SettingsAndCreatorWrapper,
  SettingsContainer,
  SettingsCreatorAndModifierSection,
  SettingsHeader,
  SettingsLoader,
  SettingsWrapper,
  getMediumDataContentId,
} from '../common';
import { displayContent } from '../common/content';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import { useLocaleSwitchLoader } from '../hooks/useLocaleSwitchLoader';
import { useTemplateReviewTakeoverContext } from './context/TemplateReviewTakeoverContext';
import { useEditableWhatsApp } from './hooks/useEditableWhatsApp';
import { PreviewPane } from './preview/PreviewPane';
import { WhatsAppStatusBanner } from './WhatsAppStatusBanner';
import { ReviewTemplateTakeover } from './ReviewTemplateTakeover';
import { SettingsForm } from './SettingsForm';
import { BI_IMAGE_HEADER_EDITABLE_STATUSES } from './constants';
import { WhatsAppInfoBanner } from './WhatsAppInfoBanner';

export const TEMPLATE_EDITOR_WHATS_APP_SETTINGS_TEST_ID = 'template-editor-whats-app-settings';

const StyledSettingsContainer = styled(SettingsContainer)`
  min-height: 0;
`;

export const SettingsEditorCore = ({ editEnabled = true }: { editEnabled?: boolean }) => {
  const { isJourneyAssociated } = useQueryParamContext();
  const { isLoading } = useLocaleSwitchLoader();
  const { medium } = useTemplateQueryContext();
  const {
    isEditMode,
    liveTemplate,
    initialTemplate: { createdAt, updatedAt },
  } = useEditableFieldsContext();
  const { isEditableWhatsAppTemplate: isEditable } = useEditableWhatsApp();
  const isNewTemplate = !!createdAt && !!updatedAt && createdAt === updatedAt;

  const whatsAppData = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const contentId = getMediumDataContentId(medium, liveTemplate);
  const { header, metaApprovalStatus } = whatsAppData;

  const hasImgHeader = !!header && 'mediaUrl' in header;
  const editableStatusesWithImgHeader = new Set([
    MetaApprovalStatusDefinition.NotSubmitted,
    ...BI_IMAGE_HEADER_EDITABLE_STATUSES,
  ]);
  /** If a template has an image header, we need to override the evaluation of the useEditableWhatsApp hook
   * for the edit button (review button still follows the logic of the useEditableWhatsApp hook). */
  const canEditImgHeader =
    hasImgHeader && metaApprovalStatus && editableStatusesWithImgHeader.has(metaApprovalStatus);

  return (
    <EditorContentContainer>
      <SettingsAndCreatorWrapper>
        <StyledSettingsContainer
          data-test={TEMPLATE_EDITOR_WHATS_APP_SETTINGS_TEST_ID}
          isEditMode={isEditMode}
        >
          <SettingsWrapper>
            <SettingsHeader>
              {displayContent.headerDetails}
              {editEnabled && (isEditable || canEditImgHeader) && <DetailsEditSaveButton />}
            </SettingsHeader>
            {!isJourneyAssociated && !isNewTemplate && <ErrorWarningBanner />}
            <SettingsForm key={contentId} />
          </SettingsWrapper>
          {isLoading && <SettingsLoader isLoading={true} />}
        </StyledSettingsContainer>
        <SettingsCreatorAndModifierSection />
      </SettingsAndCreatorWrapper>
      <PreviewPane />
    </EditorContentContainer>
  );
};

export const Settings = () => {
  const { isTemplateReviewTakeoverOpen, setIsTemplateReviewTakeoverOpen } =
    useTemplateReviewTakeoverContext();
  const { activeWhatsAppTemplateLocales } = useLocalesQueryContext();

  const hasUneditableLocales = useMemo(() => {
    return activeWhatsAppTemplateLocales?.some((val) => {
      if (!val?.metaApprovalStatus) {
        return false;
      }
      return [
        MetaApprovalStatusDefinition.Approved,
        MetaApprovalStatusDefinition.Pending,
        MetaApprovalStatusDefinition.Rejected,
      ].includes(val?.metaApprovalStatus as MetaApprovalStatusDefinition);
    });
  }, [activeWhatsAppTemplateLocales]);

  return (
    <DefaultLayout>
      <LayoutContentContainer>
        <EditableHeader isNameInputDisabled={hasUneditableLocales} />
        <WhatsAppStatusBanner />
        <WhatsAppInfoBanner />
        <SettingsEditorCore />
        {isTemplateReviewTakeoverOpen && (
          <ReviewTemplateTakeover onClose={() => setIsTemplateReviewTakeoverOpen(false)} />
        )}
      </LayoutContentContainer>
    </DefaultLayout>
  );
};
